import TicketCuticle from '@/components/manage/TicketCuticle';
import { TransText } from '@/i18n/trans/text';
import type { TravelAccountConsumptionDTO } from '@/types/dto/agent';
import { breakpoints } from '@/utils/breakpoints';
import { format, formatTime } from '@/utils/dateTime';
import { Button, Icons, Typography } from '@fleet/webshop-ui';
import type { FC } from 'react';
import { useMediaQuery } from 'react-responsive';

interface TravelPassConsumptionCardProps {
  consumption: TravelAccountConsumptionDTO;
  onViewDetails: (bookingId: string) => void;
  isLoading: boolean;
  isDisabled: boolean;
}

const TravelPassConsumptionCard: FC<TravelPassConsumptionCardProps> = ({
  consumption,
  onViewDetails,
  isLoading,
  isDisabled,
}) => {
  const isLaptopOrBigger = useMediaQuery({
    minWidth: breakpoints.laptop,
  });

  const { bookingSummary, consumedOn, tripSummary, bookingId } = consumption;

  const consumptionDate = consumedOn && format(consumedOn, 'EEE, dd MMM yyyy');

  return (
    <div className="flex flex-col rounded-lg">
      <div className="flex justify-between rounded-t-lg border-x border-b border-t border-primary bg-primary-pale p-3 laptop:p-4">
        <div className="flex flex-col gap-0.5 laptop:flex-row laptop:items-center laptop:gap-2">
          <Typography
            variant={isLaptopOrBigger ? 'subtitle' : 'body1-bold'}
            className="text-dark"
          >
            {bookingSummary}
          </Typography>
          {consumptionDate && (
            <Typography
              variant={isLaptopOrBigger ? 'body1' : 'body2'}
              className="text-neutral"
            >
              {consumptionDate}
            </Typography>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-4 border-x border-neutral-light bg-white p-3 pb-6 text-dark laptop:flex-row laptop:p-4">
        <div className="flex flex-col gap-1">
          <Typography variant="body1-bold" className="text-primary">
            {tripSummary?.summary || bookingSummary}
          </Typography>

          {tripSummary && (
            <div className="flex gap-0.5">
              {tripSummary.startTime && (
                <>
                  <Typography variant="body2" className="text-neutral">
                    {format(tripSummary.startTime, 'EEE, dd MMM yyyy')}
                  </Typography>
                  <Typography variant="body2" className="text-neutral">
                    &#8226;
                  </Typography>
                  <Typography variant="body2" className="text-neutral">
                    {formatTime(tripSummary.startTime)}
                  </Typography>
                </>
              )}
              {Boolean(tripSummary.transfers) && (
                <>
                  <Typography variant="body2" className="text-neutral">
                    &#8226;
                  </Typography>
                  <Typography variant="body2" className="text-error-text">
                    <TransText
                      i18nKey="transfersCount"
                      values={{ count: tripSummary.transfers }}
                    />
                  </Typography>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      <TicketCuticle isVertical={false} className={{ dot: 'bg-white' }} />

      <div className="flex flex-col gap-2 rounded-b-lg border-x border-b border-neutral-light bg-white p-3 pt-6 shadow-md laptop:grid laptop:grid-cols-3 laptop:p-4">
        <Button
          data-testid="consumption-view-details"
          size="medium"
          variant="secondary"
          className="laptop:col-start-3"
          onClick={() => onViewDetails(bookingId)}
          isDisabled={isDisabled}
          isPending={isLoading}
          endAdornment={<Icons.arrowRight width={20} height={20} />}
        >
          <TransText i18nKey="viewDetails" />
        </Button>
      </div>
    </div>
  );
};

export default TravelPassConsumptionCard;
